/**
 * card-scroller-window — a product card only builds its image gallery while it's near
 * the viewport. Touch devices only; desktop keeps Flickity untouched.
 *
 * On touch, each card's gallery is an `overflow-x: auto` + scroll-snap slider holding up
 * to five full-size slides. iOS composites every scroll container with its own backing
 * store, so a PLP that has appended a few hundred cards holds a few hundred of them and
 * the OS kills the tab — the self-reload on long scrolls. Measured on an iPhone: dead at
 * 88 cards with ~763MB of layer surface and only 44MB of images. Images were never the
 * constraint; 296MB of them survived fine once the scrollers were gone.
 *
 * Three rules, at three distances:
 *   - on screen: a scroll container, starting two slides wide and growing to full width
 *     on first contact (the backing store is proportional to the scrollable width)
 *   - beyond a viewport: keep the first slide, detach the rest, which takes their
 *     images with them
 *   - beyond three viewports: release the card's secondary images — ghost, swatch,
 *     siblings. Never the one the shopper actually sees: re-fetching that showed as a
 *     blank card on a slow connection, since the restore has to complete before the
 *     card arrives. Measured without any release, a 413-card scroll held 196MB of
 *     images; with it, ~10MB. Keeping the visible one costs ~65MB of that back and
 *     makes a blank card impossible.
 * The inactive mode's image is deliberately left alone. `.product-item__bg--ghost` is
 * display:none and its lazy image is never deferred, so it is ~40MB of waste — but
 * collection-model-switcher.js swaps src *between* the two images rather than toggling
 * them. Releasing the ghost means either the swap moves a placeholder into the visible
 * slot (a card that can never recover) or every image on the page is re-requested at
 * once to make the swap safe, which fails enough of them to be worse. Fixing it
 * properly means changing the switcher to swap from data attributes.
 *
 * Arming the scroller on touch instead of visibility was tried and cost the first swipe:
 * WebKit picks the scrolling node when the gesture starts.
 *
 * Hermès and DVF's grids work the same way: a slider only for the cards you can see.
 *
 * Ref: ../vault/04-engineering/patterns/plp-infinite-scroll-and-swatch-fetch.md
 * Measuring it: docs/infinite-scroll-debugger.md
 */
(function () {
  if (!window.theme || !window.theme.touch) return; // desktop uses Flickity

  // Lets the debugger show which build is actually deployed.
  window.__rsCardWindow = 'near100/far300keepvisible/onscreen0/thumb40/unstick/forget';

  const NEAR = '100% 0px'; // slides come back a viewport ahead
  const FAR = '300% 0px'; // images are released three viewports out, restored on the way in
  const ONSCREEN = '0px'; // scroll containers only while the card is actually visible
  const READY_SLIDES = 2; // enough to swipe into; the rest arrive on first contact
  const CARD = '[data-grid-item]';
  const SLIDER = '[data-hover-slider]';
  const LIVE = 'card-scroller-live';
  const TINY = 40; // px wide — roughly 5% of the decoded size, and still a picture

  // A released image keeps showing its product, just small. Deliberately not a 1x1:
  // collection-model-switcher swaps src *between* a card's two images, including on the
  // cards infinite scroll appends, so a placeholder can land in the visible slot with no
  // click for us to hook. A thumbnail degrades there instead of going blank.
  const thumbnail = (src) => {
    if (!src || (src.indexOf('/cdn/') === -1 && src.indexOf('shopify.com') === -1)) return null;
    try {
      const url = new URL(src, window.location.href);
      url.searchParams.delete('height'); // keep it proportional
      url.searchParams.delete('crop');
      url.searchParams.set('width', String(TINY));
      return url.toString();
    } catch (e) {
      return null;
    }
  };

  // The image the shopper sees: the first slide on touch, where CSS hides the static
  // background, or that background otherwise. Never released — it has to be there the
  // moment the card is, and a re-fetch cannot be relied on to beat the scroll.
  const visibleImage = (card) => {
    const slider = card.querySelector(SLIDER);
    const firstSlide = slider && slider.firstElementChild;
    return (firstSlide && firstSlide.querySelector('img')) || card.querySelector('img');
  };

  // The theme hides every image with `.is-loading` (opacity: 0.000001) and strips the
  // class in a document-level `load` listener. An image that finishes loading while
  // detached — which is exactly what parking slides does — never reaches that listener,
  // so it comes back loaded, invisible and stuck. Mirror what the theme would have done.
  const markLoaded = (img) => {
    if (!img || !img.complete || img.naturalWidth === 0) return;
    img.classList.remove('is-loading');
    const parent = img.parentNode;
    if (parent && parent.classList) parent.classList.remove('is-loading');
    if (parent && parent.parentNode && parent.parentNode.classList) {
      parent.parentNode.classList.remove('is-loading');
    }
  };

  // Never touch an image still fetching: that aborts it and can leave the element
  // complete with no pixels.
  const release = (img) => {
    if (!img || img.dataset.rsSrc || !img.complete) return;
    const src = img.getAttribute('src');
    const small = thumbnail(src);
    if (!small) return;
    img.dataset.rsSrc = src;
    if (img.getAttribute('srcset')) img.dataset.rsSrcset = img.getAttribute('srcset');
    img.removeAttribute('srcset');
    img.setAttribute('src', small);
  };

  const restore = (img) => {
    if (!img.dataset.rsSrc) return;
    if (img.dataset.rsSrcset) {
      img.setAttribute('srcset', img.dataset.rsSrcset);
      delete img.dataset.rsSrcset;
    }
    img.setAttribute('src', img.dataset.rsSrc);
    delete img.dataset.rsSrc;
    markLoaded(img);
  };

  const collapse = (card) => {
    const slider = card.querySelector(SLIDER);
    if (!slider) return;
    const attached = Array.from(slider.children).slice(1);
    attached.forEach((slide) => slide.remove());
    // Anything still detached belongs after what was just taken out.
    card.rsSlides = attached.concat(card.rsSlides || []);
    card.classList.remove(LIVE);
  };

  // Put back some slides now, the rest when the shopper engages with this card.
  const attach = (card, count) => {
    if (!card.rsSlides || !card.rsSlides.length) return;
    const slider = card.querySelector(SLIDER);
    if (!slider) return;
    const batch = count === undefined ? card.rsSlides : card.rsSlides.slice(0, count);
    batch.forEach((slide) => slider.appendChild(slide));
    card.rsSlides = card.rsSlides.slice(batch.length);
    // Slides past the first sit outside the slider's scrollport, where `loading="lazy"`
    // would hold them until the shopper had already swiped there.
    slider.querySelectorAll('img').forEach((img) => {
      if (img.getAttribute('loading') === 'lazy') img.setAttribute('loading', 'eager');
      markLoaded(img); // it may have finished while detached
      img.addEventListener('load', () => markLoaded(img), { once: true });
    });
  };

  const init = () => {
    const grids = Array.from(document.querySelectorAll('[data-products-grid]'));
    if (!grids.length) return;


    const proximity = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) attach(entry.target, READY_SLIDES - 1); // slide 0 never left
          else collapse(entry.target);
        });
      },
      { rootMargin: NEAR }
    );

    // Images are the last thing to go and the first to come back.
    const distant = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const card = entry.target;
          if (entry.isIntersecting) {
            card.querySelectorAll('img').forEach((img) => {
              restore(img);
              markLoaded(img);
            });
          } else {
            const keep = visibleImage(card);
            card.querySelectorAll('img').forEach((img) => {
              if (img !== keep) release(img);
            });
          }
        });
      },
      { rootMargin: FAR }
    );

    // The scroller has to exist before the gesture starts, so it follows visibility.
    const onscreen = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => entry.target.classList.toggle(LIVE, entry.isIntersecting));
      },
      { rootMargin: ONSCREEN }
    );

    // The switcher exchanges attributes between the two images, so a released image must
    // never be in play when it runs. Only the released ones need touching — walking every
    // image on the page meant ~1,500 attribute writes and a burst of requests.
    document.addEventListener('click', (event) => {
      if (!event.target.closest || !event.target.closest('.collection__model--button')) return;
      grids.forEach((grid) => grid.querySelectorAll('img[data-rs-src]').forEach(restore));
    }, true);

    // First contact with a card: give it the rest of its slides.
    document.addEventListener('touchstart', (event) => {
      const card = event.target.closest && event.target.closest(CARD);
      if (card) attach(card);
    }, { capture: true, passive: true });

    // And if a swipe somehow starts before that lands, top the card up.
    document.addEventListener('scroll', (event) => {
      const slider = event.target;
      if (slider && slider.matches && slider.matches(SLIDER)) {
        const card = slider.closest(CARD);
        if (card) attach(card);
      }
    }, { capture: true, passive: true });

    // A filter or sort rebuilds the grid, and IntersectionObserver holds a strong
    // reference to every target — so cards must be let go of or they are retained,
    // detached slides and all, for the life of the page.
    const forget = (root) => {
      if (!root.querySelectorAll) return;
      const cards = root.matches && root.matches(CARD) ? [root] : root.querySelectorAll(CARD);
      cards.forEach((card) => {
        proximity.unobserve(card);
        onscreen.unobserve(card);
        distant.unobserve(card);
        card.rsSlides = null;
        delete card.dataset.rsScrollerManaged;
      });
    };

    const adopt = (root) => {
      const cards = root.matches && root.matches(CARD) ? [root] : root.querySelectorAll(CARD);
      cards.forEach((card) => {
        if (card.dataset.rsScrollerManaged) return;
        card.dataset.rsScrollerManaged = '1';
        collapse(card); // start collapsed; the observers restore what's in range
        proximity.observe(card);
        onscreen.observe(card);
        distant.observe(card);
      });
    };

    grids.forEach((grid) => {
      adopt(grid);
      // Infinite scroll appends cards straight into the grid.
      new MutationObserver((records) => {
        records.forEach((record) => {
          record.removedNodes.forEach((node) => {
            if (node.nodeType === 1) forget(node);
          });
          record.addedNodes.forEach((node) => {
            if (node.nodeType === 1) adopt(node);
          });
        });
      }).observe(grid, { childList: true, subtree: true });
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
